/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, useState, useContext } from 'react';
import { addWeeks, getTodaysDate } from 'school-space-utils/date';

import {
    Agenda,
    BookingTypePicker,
    Button,
    Column,
    DatePicker,
    Heading,
    Loading,
    Row,
    Select,
    Spacer,
    UserContext
} from 'school-space-client/components';
import { getSchoolCalendar } from 'school-space-client/api';

const css = {
    buttons: theme => ({
        display: 'none',
        justifyContent: 'space-betweeen',
        flexWrap: 'no-wrap',
        [theme.helpers.print]: {
            display: 'none'
        },
        [theme.helpers.mq('m')]: {
            display: 'flex'
        }
    }),
    print: theme => ({
        paddingTop: theme.layout.baseline * 0.75,
        paddingBottom: theme.layout.baseline * 0.75,
        paddingLeft: 0,
        paddingRight: 0
    }),
    row: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    select: theme => ({
        marginRight: theme.layout.baseline * 2
    })
};

const today = getTodaysDate();
const fourWeeksTime = addWeeks(today, 4);

const UpcomingBookings = () => {
    const { id: schoolId, sites, spaces } = useContext(UserContext);

    const [siteId, setSiteId] = useState(null);
    const [spaceId, setSpaceId] = useState(null);
    const [typeId, setTypeId] = useState(null);

    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(fourWeeksTime);

    const [fetching, setFetching] = useState(false);
    const [calendarEvents, setCalendarEvents] = useState({});

    useEffect(() => {
        setFetching(true);

        getSchoolCalendar(schoolId, {
            ...(siteId && { siteId }),
            ...(spaceId && { spaceId }),
            ...(typeId && { eventType: typeId }),
            fromDate,
            toDate,
            groupBy: ['dateString']
        })
            .then(data => {
                setCalendarEvents(data);
                return setFetching(false);
            })
            .catch(error => {
                setFetching(false);
                setCalendarEvents(() => {
                    throw error;
                });
            });
    }, [schoolId, siteId, spaceId, typeId, fromDate, toDate]);

    return (
        <main>
            <Row>
                <Column s={[6]} m={[10]} l={[10]}>
                    <Heading level="1" size="x-large">
                        Upcoming bookings
                    </Heading>
                </Column>
                <Column styles={css.buttons} s={[6]} m={[2]} l={[2]}>
                    <Button state="ghost" onClick={() => window.print()} styles={css.print}>
                        Print
                    </Button>
                </Column>
            </Row>
            <Spacer height={1.5} />
            <div css={css.row}>
                <div css={css.select}>
                    <DatePicker
                        name="from-date"
                        label="From"
                        value={fromDate}
                        onChange={setFromDate}
                        min="2019-09-01"
                    />
                </div>
                <div css={css.select}>
                    <DatePicker
                        name="to-date"
                        label="To"
                        value={toDate}
                        onChange={setToDate}
                        min={today}
                    />
                </div>
            </div>
            <Spacer height={0.5} />
            <div css={css.row}>
                {sites && (
                    <div css={css.select}>
                        <Select
                            name="site"
                            label="Site"
                            placeholder="All sites"
                            value={sites.find(site => site.id === siteId)}
                            onChange={site => {
                                setSiteId(site ? site.id : null);
                                setSpaceId(null);
                            }}
                            options={sites}
                            isClearable
                        />
                    </div>
                )}
                <div css={css.select}>
                    <Select
                        name="space"
                        label="Space"
                        placeholder="All spaces"
                        value={spaces.find(space => space.id === spaceId)}
                        onChange={space => setSpaceId(space ? space.id : null)}
                        options={spaces}
                        isClearable
                        isSearchable
                    />
                </div>
                <div css={css.select}>
                    <BookingTypePicker value={typeId} onChange={setTypeId} isClearable />
                </div>
            </div>
            <Spacer height={1} />
            {fetching ? (
                <Loading />
            ) : (
                <Agenda
                    eventsByDate={calendarEvents}
                    fallbackMessage="No bookings found with the selected filters"
                />
            )}
        </main>
    );
};

export default UpcomingBookings;
